import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { TASK_STATUSES, TASK_STATUS_DOT, PRIORITY_BADGE, fmtDate, kanbanOrder, isOverdueTask, jstTodayStr, dispText } from '../plannerConstants';
import { User, Warning } from '@phosphor-icons/react';

// Simple Kanban with native HTML5 drag-and-drop between status columns.
export default function KanbanBoard({ tasks = [], onMove, onCardClick }) {
  const { t } = useT();
  const [dragOver, setDragOver] = useState(null);

  // 常に描画前にソート — DB順・ドラッグ履歴に依存しない（期限超過→期限昇順→優先度→作成日→タイトル）
  const today = jstTodayStr();
  const order = kanbanOrder(today);
  const cols = TASK_STATUSES.map((st) => ({ st, items: tasks.filter((tk) => tk.status === st).slice().sort(order) }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="planner-kanban">
      {cols.map(({ st, items }) => (
        <div key={st}
          onDragOver={(e) => { e.preventDefault(); setDragOver(st); }}
          onDragLeave={() => setDragOver((d) => (d === st ? null : d))}
          onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/plain'); setDragOver(null); if (id) onMove?.(id, st); }}
          className={`rounded-xl border p-3 min-h-[200px] transition-colors ${dragOver === st ? 'border-indigo-400 bg-indigo-50/50' : 'border-zinc-200 bg-zinc-50/60'}`}
          data-testid={`kanban-col-${st}`}>
          <div className="flex items-center gap-2 mb-3 px-1">
            <span className={`w-2 h-2 rounded-full ${TASK_STATUS_DOT[st]}`} />
            <span className="text-sm font-semibold text-zinc-700">{t(`planner.taskStatus.${st}`)}</span>
            <span className="ml-auto text-xs text-zinc-400 tabular-nums">{items.length}</span>
          </div>
          <div className="space-y-2">
            {items.map((tk) => {
              const overdue = isOverdueTask(tk, today);
              return (
              <div key={tk.id} draggable
                onDragStart={(e) => e.dataTransfer.setData('text/plain', tk.id)}
                onClick={() => onCardClick?.(tk)}
                data-testid={`kanban-card-${tk.id}`}
                className={`bg-white rounded-lg border p-3 shadow-sm cursor-pointer hover:shadow-md transition-all active:scale-[0.99] ${
                  overdue ? 'border-rose-300 hover:border-rose-400' : 'border-zinc-200 hover:border-indigo-200'}`}>
                <p className="text-sm font-medium text-zinc-800 leading-snug mb-2"><span data-no-translate>{dispText(tk.title)}</span></p>
                <div className="flex items-center gap-2 flex-wrap">
                  {overdue && (
                    <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200 text-[10px] px-1.5 py-0 font-bold"
                      data-testid={`kanban-overdue-${tk.id}`}>{t('planner.stat.overdue')}</Badge>
                  )}
                  <Badge variant="outline" className={`${PRIORITY_BADGE[tk.priority]} text-[10px] px-1.5 py-0`}>{t(`planner.priority.${tk.priority}`)}</Badge>
                  {tk.due_date && (
                    <span className={`inline-flex items-center gap-1 text-[11px] ${overdue ? 'text-rose-600 font-semibold' : 'text-zinc-500'}`}>
                      {/* 一覧と同じ判定 — 期限超過かつ未完了のときだけ警告を出す */}
                      {overdue && <Warning size={11} weight="fill" className="text-rose-500" data-testid={`kanban-warn-${tk.id}`} />}
                      {fmtDate(tk.due_date)}
                    </span>
                  )}
                  {tk.member_names && tk.member_names.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 ml-auto"><User size={11} />{tk.member_names[0]}{tk.member_names.length > 1 ? ` +${tk.member_names.length - 1}` : ''}</span>
                  )}
                </div>
              </div>
              );
            })}
            {items.length === 0 && <p className="text-xs text-zinc-300 text-center py-4">—</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
